import { useState, useEffect, useRef, useCallback } from "react";
import { bandRepairNotice, type BandRepairResult } from "@shared/band-repair";
import { readImageEditStatus, waitForImageEditCheck } from "@/lib/image-edit-observation";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { AdImage } from "@/components/ad-image";
import { InlineRenameTitle } from "@/components/inline-rename-title";
import { sanitizeFilename } from "@/lib/filename";
import { apiRequest, invalidateGeneratedAdListings, queryClient } from "@/lib/queryClient";
import { invalidateApprovalStates } from "@/lib/approval-states";
import { InpaintingModal } from "@/components/campaign-wizard/inpainting-modal";
import { useAuth } from "@/hooks/use-auth";
import { findPendingImageEdit, clearPendingImageEdit, imageEditPhaseLabel, updatePendingImageEdit, persistPendingImageEdit, readPendingImageEdits } from "@/lib/pending-image-edits";
import { usePendingImageEditsRevision } from "@/hooks/use-pending-image-edits";
import { UndoAdEditButton, RedoAdEditButton, useUndoableAdIds, useRedoableAdIds, invalidateUndoableAds } from "@/components/undo-ad-edit";
import {
  History, ChevronDown, ChevronUp, Loader2, Download, Bookmark, BookmarkCheck,
  Maximize2, XCircle, Users, Package, Sparkles, Wand2, CalendarClock, Pencil, RotateCcw,
} from "lucide-react";

// Tile aspect classes mirror the main current-run grid (ASPECT_CLASS in
// ads-express.tsx) so session tiles render at their real ratio instead of
// always 4:5. Unknown/legacy ratios fall back to 4:5.
const SESSION_ASPECT_CLASS: Record<string, string> = {
  "1:1": "aspect-square",
  "4:5": "aspect-[4/5]",
  "9:16": "aspect-[9/16]",
  "16:9": "aspect-[16/9]",
  "2:3": "aspect-[2/3]",
};

// ── Types ────────────────────────────────────────────────────────────────────

type SessionStatus = "queued" | "running" | "completed" | "partial" | "failed" | "cancelled";

interface SessionSummary {
  id: string;
  status: SessionStatus;
  createdAt: string | null;
  completedAt: string | null;
  personaNameSnapshot: string | null;
  productNameSnapshot: string | null;
  angleNameSnapshot: string | null;
  creativeDirection: string | null;
  personaCharacterSelectorEnabled?: boolean | null;
  selectedPersonaCharacterNameSnapshot?: string | null;
  aspectRatio: string | null;
  templateCount: number;
}

interface SessionAd {
  id: string;
  adUnitTypeId: string | null;
  // Template name captured at creation (survives template renames/deletes).
  adUnitNameSnapshot?: string | null;
  aspectRatio: string | null;
  status: "pending" | "rendering" | "queued" | "ready" | "failed" | "cancelled";
  imageUrlOriginal: string | null;
  imageUrlSaved: string | null;
  errorMessage: string | null;
  isSaved: boolean;
  customName: string | null;
  // Task #1572 — auto-generated convention name; null on legacy rows.
  defaultNameSnapshot: string | null;
  // Task #2181 — sale/discount applied to this ad's run; null = no sale.
  salesDiscountNameSnapshot?: string | null;
}

const STATUS_META: Record<SessionStatus, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  queued: { label: "Queued", variant: "secondary" },
  running: { label: "Running", variant: "secondary" },
  completed: { label: "Completed", variant: "default" },
  partial: { label: "Partial", variant: "outline" },
  failed: { label: "Failed", variant: "destructive" },
  cancelled: { label: "Cancelled", variant: "outline" },
};

function formatWhen(iso: string | null): string {
  if (!iso) return "Unknown date";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

// ── Expanded session ads grid ────────────────────────────────────────────────

export function SessionAdsGrid({
  sessionId,
  adUnitNameById,
  brandId,
}: {
  sessionId: string;
  adUnitNameById: Map<string, string>;
  brandId?: string;
}) {
  const { toast } = useToast();
  const { user: authUser } = useAuth();
  const isMaster = authUser?.isMaster === true;
  // Task #2040 — undo/redo-last-edit availability for saved ads in this session.
  const undoableAdIds = useUndoableAdIds(brandId);
  const redoableAdIds = useRedoableAdIds(brandId);
  const [lightbox, setLightbox] = useState<{ adId: string; url: string; name: string } | null>(null);
  // Task #1858 — master-only 9:16 band repair on rendered (saved or unsaved)
  // ads: re-renders ONLY the top/bottom bands, keeps the creative untouched.
  const [fixingBandIds, setFixingBandIds] = useState<Set<string>>(new Set());
  const fixingBandsRef = useRef(new Set<string>());
  const fixBands = async (adId: string) => {
    if (fixingBandsRef.current.has(adId)) return;
    fixingBandsRef.current.add(adId);
    setFixingBandIds((prev) => new Set(prev).add(adId));
    try {
      const res = await apiRequest("POST", `/api/generated-ads/${adId}/repair-bands`, {});
      const repaired = await res.json() as BandRepairResult;
      // Use the committed URL immediately, including already-open previews.
      setEditedUrls((prev) => ({ ...prev, [adId]: repaired.durableUrl }));
      setLightbox((prev) => prev?.adId === adId ? { ...prev, url: repaired.durableUrl } : prev);
      setEditingAd((prev) => prev?.id === adId ? { ...prev, url: repaired.durableUrl } : prev);
      invalidateUndoableAds(brandId);
      await Promise.all([
        invalidateApprovalStates(queryClient, brandId),
        invalidateGeneratedAdListings(),
        queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] }),
      ]);
      toast(bandRepairNotice(repaired));
    } catch (e: unknown) {
      toast({
        title: "Band repair failed",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      fixingBandsRef.current.delete(adId);
      setFixingBandIds((prev) => {
        const next = new Set(prev);
        next.delete(adId);
        return next;
      });
    }
  };
  // In-place edits mirror the current-run flow: the edited image lives in a
  // local override until the user saves (the save endpoint then persists the
  // on-screen version instead of the original render).
  const [editedUrls, setEditedUrls] = useState<Record<string, string>>({});
  const [editingAd, setEditingAd] = useState<{ id: string; url: string; name: string; isSaved: boolean } | null>(null);
  const [editBusy, setEditBusy] = useState<Set<string>>(new Set());
  // Phase reported by the edit job while pending ("rendering"/"saving") so
  // the busy overlay can show the same phase-aware label as the main grid.
  const [editPhases, setEditPhases] = useState<Record<string, string | null>>({});
  const pollingRef = useRef<Set<string>>(new Set());
  const editingAdRef = useRef<typeof editingAd>(null);
  useEffect(() => { editingAdRef.current = editingAd; }, [editingAd]);
  // Unmount guard: the background poller must stop touching state (and stop
  // clearing the persisted pending job) once the grid is gone, so a later
  // mount can re-attach cleanly. Matches the main grid's mountedRef.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const { data, isLoading, isError } = useQuery<{ generation: unknown; ads: SessionAd[] }>({
    queryKey: ["/api/ad-generations", sessionId],
    staleTime: 30_000,
    // A per-tile Retry reopens the run — keep polling while any ad is
    // non-terminal so the tile flips to the fresh render when it lands.
    refetchInterval: (q) =>
      (q.state.data?.ads ?? []).some(
        (a) => a.status === "pending" || a.status === "rendering" || a.status === "queued",
      )
        ? 5000
        : false,
  });

  const saveMutation = useMutation({
    mutationFn: ({ adId, imageUrl }: { adId: string; imageUrl?: string }) =>
      apiRequest("POST", `/api/generated-ads/${adId}/save`, imageUrl ? { imageUrl } : {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
      invalidateGeneratedAdListings();
      toast({ title: "Saved to My Ads" });
    },
    onError: (e: any) =>
      toast({ title: "Couldn't save", description: e?.message, variant: "destructive" }),
  });

  // Optimistic rename overrides so the title updates instantly; the PATCH
  // persists it (same row that save-to-library flips, so the name carries
  // into My Ads if they save the ad afterwards).
  const [nameOverrides, setNameOverrides] = useState<Record<string, string | null>>({});
  const handleRename = async (adId: string, templateName: string, name: string | null) => {
    const prevOverride = nameOverrides[adId];
    const hadOverride = adId in nameOverrides;
    setNameOverrides((prev) => ({ ...prev, [adId]: name }));
    setLightbox((lb) => (lb?.adId === adId ? { ...lb, name: name?.trim() || templateName } : lb));
    try {
      await apiRequest("PATCH", `/api/generated-ads/${adId}`, { customName: name });
      queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
    } catch (e: any) {
      setNameOverrides((prev) => {
        const next = { ...prev };
        if (hadOverride) next[adId] = prevOverride; else delete next[adId];
        return next;
      });
      toast({ title: "Couldn't rename", description: e?.message, variant: "destructive" });
      throw e;
    }
  };

  // Per-tile Retry for failed/cancelled ads — same flow as the main grid:
  // flip the row back to pending, then (re)start the run's worker. The
  // server reopens terminal runs when a child is pending again.
  const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
  const retryAd = async (adId: string) => {
    if (!brandId) return;
    setRetryingIds((prev) => new Set(prev).add(adId));
    try {
      await apiRequest("PATCH", `/api/generated-ads/${adId}`, { status: "pending", errorMessage: null });
      await apiRequest("POST", `/api/brands/${brandId}/ad-generations/${sessionId}/start`, {});
      queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Retry failed", description: msg, variant: "destructive" });
    } finally {
      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(adId);
        return next;
      });
    }
  };

  const markBusy = useCallback((adId: string, busy: boolean) => {
    setEditBusy((prev) => {
      const next = new Set(prev);
      if (busy) next.add(adId); else next.delete(adId);
      return next;
    });
  }, []);

  // Re-attach a background poller for any ad whose inline edit is still in
  // flight (edit modal closed mid-edit, or the page was refreshed). Applies
  // the result to the local override when it lands. Skips the ad whose edit
  // modal is currently open — the modal owns that poll.
  const pollEditJob = useCallback(async (jobId: string, adId: string, name: string, isSaved: boolean) => {
    // Per-tile in-flight dedupe: re-attach effects can fire repeatedly, so a
    // tile that's already being polled must never gain a second poller.
    if (pollingRef.current.has(adId)) return;
    pollingRef.current.add(adId);
    markBusy(adId, true);
    // Nudge the master Recent generations summary so its "Edits" column badge
    // picks up this in-flight edit right away instead of on the next slow poll.
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-generations/recent-summary`] });
    let outcome: { type: "ready"; url: string } | { type: "failed"; message: string };
    while (true) {
      if (!mountedRef.current) {
        pollingRef.current.delete(adId);
        return; // grid gone — leave the pending job for re-attach
      }
      try {
        const res = await readImageEditStatus(jobId);
        if (!mountedRef.current) {
          pollingRef.current.delete(adId);
          return; // unmounted while the request was in flight — no state updates
        }
        if (res.ok) {
          const status = (await res.json()) as { status: string; image_url?: string; message?: string; phase?: string };
          if (!mountedRef.current) {
            pollingRef.current.delete(adId);
            return; // unmounted while parsing the body — no state updates
          }
          if (status.status === "pending") {
            setEditPhases((prev) =>
              prev[adId] === (status.phase ?? null) ? prev : { ...prev, [adId]: status.phase ?? null },
            );
          }
          if (status.status === "ready" && status.image_url) {
            outcome = { type: "ready", url: status.image_url };
            break;
          }
          if (status.status === "failed") {
            outcome = { type: "failed", message: status.message || "Edit failed" };
            break;
          }
        }
      } catch {
        if (mountedRef.current) setEditPhases(prev => ({ ...prev, [adId]: "checking" }));
      }
      await waitForImageEditCheck(jobId);
    }
    if (!mountedRef.current) { pollingRef.current.delete(adId); return; }
    markBusy(adId, false);
    queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-generations/recent-summary`] });
    setEditPhases((prev) => {
      if (!(adId in prev)) return prev;
      const next = { ...prev };
      delete next[adId];
      return next;
    });
    // If the edit modal is open on this ad again, defer to its own poller.
    const tracked = readPendingImageEdits().find(e => e.jobId === jobId);
    if (editingAdRef.current?.id === adId || !tracked || tracked.delivery === "review") {
      pollingRef.current.delete(adId);
      return;
    }
    if (outcome.type === "ready") {
      const url = outcome.url;
      updatePendingImageEdit(jobId, { delivery: "background", resultImageUrl: url });
      // Saved ads persist the recovered edit in place (same as accepting it
      // in the modal); unsaved ads persist via accept-edit (Task #1494) so
      // the edit survives navigating away too.
      try {
        await persistPendingImageEdit(jobId, async () => {
          await apiRequest("POST", `/api/generated-ads/${adId}/${isSaved ? "edit-in-place" : "accept-edit"}`, { imageUrl: url });
        });
        invalidateApprovalStates(queryClient, brandId);
        queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
        invalidateUndoableAds(brandId);
      } catch (e: any) {
        updatePendingImageEdit(jobId, { delivery: "review", error: e?.message || "Couldn't persist edit" });
        toast({ title: "Couldn't persist edit", description: e?.message, variant: "destructive" });
        pollingRef.current.delete(adId);
        return;
      }
      if (mountedRef.current) setEditedUrls((prev) => ({ ...prev, [adId]: url }));
      toast({ title: "Edit applied", description: name });
    } else if (outcome.type === "failed") {
      updatePendingImageEdit(jobId, { delivery: "review", error: outcome.message });
      toast({ title: "Edit failed", description: outcome.message, variant: "destructive" });
    }
    pollingRef.current.delete(adId);
  }, [markBusy, toast, sessionId, brandId]);

  const ads = data?.ads ?? [];
  const imageEditsRevision = usePendingImageEditsRevision();

  useEffect(() => {
    for (const ad of ads) {
      const url = editedUrls[ad.id] ?? ad.imageUrlSaved ?? ad.imageUrlOriginal;
      if (!url) continue;
      if (editingAd?.id === ad.id) continue;
      const pending = findPendingImageEdit(url);
      if (pending && !pending.target && pending.delivery !== "review" && !pending.error && !pollingRef.current.has(ad.id)) {
        const name = ad.adUnitTypeId ? (adUnitNameById.get(ad.adUnitTypeId) ?? "Ad unit") : "Ad unit";
        void pollEditJob(pending.jobId, ad.id, name, ad.isSaved);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, editingAd, editedUrls, pollEditJob, imageEditsRevision]);

  // Task #1798 — always download via the authenticated proxy (provider hosts
  // don't send CORS headers, and downloads must never open a new tab).
  const downloadOne = async (url: string, name: string) => {
    const fileName = `${sanitizeFilename(name)}.png`;
    try {
      const proxyUrl = `/api/img-download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(fileName)}`;
      const res = await fetch(proxyUrl);
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    }
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="aspect-[4/5] rounded-lg" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <p className="text-xs text-destructive pt-3" data-testid={`text-session-error-${sessionId}`}>
        Couldn't load this session's ads.
      </p>
    );
  }

  if (ads.length === 0) {
    return (
      <p className="text-xs text-muted-foreground pt-3" data-testid={`text-session-empty-${sessionId}`}>
        No ads were generated in this session.
      </p>
    );
  }

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-3">
        {ads.map((ad) => {
          // Task #1572 — prefer the auto-generated convention name; legacy
          // rows fall back to the template name. customName still wins below.
          const templateName =
            ad.defaultNameSnapshot?.trim() ||
            (ad.adUnitTypeId ? (adUnitNameById.get(ad.adUnitTypeId) ?? "Ad unit") : "Ad unit");
          const customName = ad.id in nameOverrides ? nameOverrides[ad.id] : ad.customName;
          const name = customName?.trim() || templateName;
          // Which template produced this ad — the name snapshot captured at
          // creation, falling back to a live lookup for legacy rows.
          const templateLabel =
            ad.adUnitNameSnapshot?.trim() ||
            (ad.adUnitTypeId ? adUnitNameById.get(ad.adUnitTypeId) ?? null : null);
          const imageUrl = editedUrls[ad.id] ?? ad.imageUrlSaved ?? ad.imageUrlOriginal;
          const saving = saveMutation.isPending && saveMutation.variables?.adId === ad.id;
          const busy = editBusy.has(ad.id);
          const isWorking = ad.status === "pending" || ad.status === "rendering" || ad.status === "queued";
          const aspectClass = SESSION_ASPECT_CLASS[ad.aspectRatio ?? ""] ?? "aspect-[4/5]";
          return (
            <div
              key={ad.id}
              className="rounded-lg border border-border/50 overflow-hidden bg-card"
              data-testid={`tile-session-ad-${ad.id}`}
            >
              <div className={`relative ${aspectClass} bg-muted/40 flex items-center justify-center`}>
                {ad.status === "ready" && imageUrl ? (
                  <button
                    type="button"
                    onClick={() => setLightbox({ adId: ad.id, url: imageUrl, name })}
                    className="w-full h-full block cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-primary"
                    title="Click to view"
                    data-testid={`button-view-session-ad-${ad.id}`}
                  >
                    <AdImage
                      src={imageUrl}
                      thumbWidth={640}
                      alt={name}
                      className="w-full h-full object-cover"
                      data-testid={`img-session-ad-${ad.id}`}
                    />
                    {busy && (
                      <div
                        className="absolute inset-0 bg-background/60 backdrop-blur-[1px] flex flex-col items-center justify-center gap-2"
                        data-testid={`overlay-edit-session-ad-${ad.id}`}
                      >
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="text-[11px] font-medium text-foreground">{imageEditPhaseLabel(editPhases[ad.id])}</span>
                      </div>
                    )}
                  </button>
                ) : ad.status === "failed" || ad.status === "cancelled" ? (
                  <div className="flex flex-col items-center gap-1.5 px-3 text-center">
                    <XCircle className={`h-6 w-6 ${ad.status === "failed" ? "text-destructive" : "text-muted-foreground"}`} />
                    <span
                      className={`text-[10px] font-medium line-clamp-3 ${ad.status === "failed" ? "text-destructive" : "text-muted-foreground"}`}
                      data-testid={`text-session-ad-error-${ad.id}`}
                    >
                      {ad.status === "failed" ? (ad.errorMessage || "Failed") : "Cancelled"}
                    </span>
                  </div>
                ) : isWorking ? (
                  <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    <span className="text-[10px] font-medium" data-testid={`text-session-ad-working-${ad.id}`}>
                      {ad.status === "rendering" ? "Generating..." : "Queued — starting shortly"}
                    </span>
                  </div>
                ) : (
                  <AdImage src={null} alt={name} className="w-full h-full" />
                )}
              </div>
              <div className="p-2.5 space-y-1.5">
                <div className="text-[11px] font-medium" title={name} data-testid={`text-session-ad-name-${ad.id}`}>
                  {ad.status === "ready" ? (
                    <InlineRenameTitle
                      customName={customName}
                      fallbackName={templateName}
                      onSave={(next) => handleRename(ad.id, templateName, next)}
                      className="[&>span:first-child]:truncate"
                      inputClassName="text-[11px] font-medium"
                      testId={`session-ad-name-${ad.id}`}
                    />
                  ) : (
                    <span className="block truncate">{name}</span>
                  )}
                  {templateLabel && templateLabel !== name && (
                    <span
                      className="mt-0.5 block text-[10px] font-normal text-muted-foreground truncate"
                      title={`Template: ${templateLabel}`}
                      data-testid={`text-template-session-ad-${ad.id}`}
                    >
                      Template: {templateLabel}
                    </span>
                  )}
                  {/* Task #2181 — this ad was generated under a sale. */}
                  {!!ad.salesDiscountNameSnapshot?.trim() && (
                    <span
                      className="mt-1 inline-flex max-w-full items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-400 truncate"
                      title={`Sale: ${ad.salesDiscountNameSnapshot.trim()}`}
                      data-testid={`badge-sale-session-ad-${ad.id}`}
                    >
                      Sale: {ad.salesDiscountNameSnapshot.trim()}
                    </span>
                  )}
                </div>
                {(ad.status === "failed" || ad.status === "cancelled") && brandId && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-7 text-[11px]"
                    onClick={() => retryAd(ad.id)}
                    disabled={retryingIds.has(ad.id)}
                    title="Generate this ad again"
                    data-testid={`button-retry-session-ad-${ad.id}`}
                  >
                    {retryingIds.has(ad.id) ? (
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                      <RotateCcw className="h-3 w-3 mr-1" />
                    )}
                    Retry
                  </Button>
                )}
                {ad.status === "ready" && imageUrl && (
                  // Labeled buttons (icon + word) instead of icon-only — the
                  // icons alone weren't discoverable (user request, Task #2040).
                  <div className="flex flex-wrap items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => setLightbox({ adId: ad.id, url: imageUrl, name })}
                      title="View full size"
                      data-testid={`button-lightbox-session-ad-${ad.id}`}
                    >
                      <Maximize2 className="h-3 w-3 mr-1" /> View
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => setEditingAd({ id: ad.id, url: imageUrl, name, isSaved: ad.isSaved })}
                      disabled={busy}
                      title="Edit with AI"
                      data-testid={`button-edit-session-ad-${ad.id}`}
                    >
                      <Pencil className="h-3 w-3 mr-1" /> Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() =>
                        saveMutation.mutate({ adId: ad.id, imageUrl: editedUrls[ad.id] })
                      }
                      disabled={ad.isSaved || saving || busy}
                      title={ad.isSaved ? "Saved" : "Save to My Ads"}
                      data-testid={`button-save-session-ad-${ad.id}`}
                    >
                      {saving ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : ad.isSaved ? (
                        <BookmarkCheck className="h-3 w-3 mr-1" />
                      ) : (
                        <Bookmark className="h-3 w-3 mr-1" />
                      )}
                      {ad.isSaved ? "Saved" : "Save"}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px]"
                      onClick={() => downloadOne(imageUrl, name)}
                      title="Download"
                      data-testid={`button-download-session-ad-${ad.id}`}
                    >
                      <Download className="h-3 w-3 mr-1" /> Download
                    </Button>
                    {brandId && (
                      <>
                        <UndoAdEditButton
                          adId={ad.id}
                          brandId={brandId}
                          undoableAdIds={undoableAdIds}
                          className="h-7 px-2 text-[11px]"
                          onUndone={(durableUrl) => {
                            // Drop any local edited override so the restored
                            // version wins, and refresh the session listing.
                            setEditedUrls((prev) => {
                              const next = { ...prev };
                              delete next[ad.id];
                              return next;
                            });
                            setLightbox((lb) =>
                              lb?.adId === ad.id ? { ...lb, url: durableUrl } : lb,
                            );
                            queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
                          }}
                        />
                        <RedoAdEditButton
                          adId={ad.id}
                          brandId={brandId}
                          redoableAdIds={redoableAdIds}
                          className="h-7 px-2 text-[11px]"
                          onRedone={(durableUrl) => {
                            setEditedUrls((prev) => {
                              const next = { ...prev };
                              delete next[ad.id];
                              return next;
                            });
                            setLightbox((lb) =>
                              lb?.adId === ad.id ? { ...lb, url: durableUrl } : lb,
                            );
                            queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
                          }}
                        />
                      </>
                    )}
                    {isMaster && ad.aspectRatio === "9:16" && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px]"
                        onClick={() => fixBands(ad.id)}
                        disabled={fixingBandIds.has(ad.id) || busy}
                        title="Fix 9:16 bands (re-renders only the top/bottom bands)"
                        data-testid={`button-fix-bands-session-ad-${ad.id}`}
                      >
                        {fixingBandIds.has(ad.id) ? (
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                        ) : (
                          <Wand2 className="h-3 w-3 mr-1" />
                        )}
                        Fix bands
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-background" data-testid="dialog-session-lightbox">
          <DialogTitle className="sr-only">{lightbox?.name ?? "Ad image"}</DialogTitle>
          {lightbox && (
            <div className="flex flex-col">
              <div className="bg-muted/30 flex items-center justify-center max-h-[80vh] overflow-auto">
                <AdImage
                  src={lightbox.url}
                  alt={lightbox.name}
                  className="max-w-full max-h-[80vh] object-contain"
                  data-testid="img-session-lightbox"
                />
              </div>
              <div className="flex items-center justify-between gap-3 p-3 border-t border-border/50">
                <div className="text-xs font-medium truncate">{lightbox.name}</div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadOne(lightbox.url, lightbox.name)}
                  data-testid="button-download-session-lightbox"
                >
                  <Download className="h-3.5 w-3.5 mr-1.5" /> Download
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {editingAd && (
        <InpaintingModal
          open={!!editingAd}
          onClose={() => setEditingAd(null)}
          imageUrl={editingAd.url}
          adId={editingAd.id}
          brandId={brandId}
          onAccept={async (newUrl) => {
            const id = editingAd.id;
            // Already-saved ads persist the edit immediately (replace-in-place,
            // same as the library edit flow). Unsaved ads persist too via the
            // lightweight accept-edit endpoint (Task #1494) so the edit
            // survives navigating away — the local override still swaps the
            // tile instantly.
            if (editingAd.isSaved) {
              await apiRequest("POST", `/api/generated-ads/${id}/edit-in-place`, { imageUrl: newUrl });
              invalidateApprovalStates(queryClient, brandId);
              invalidateUndoableAds(brandId);
            } else {
              await apiRequest("POST", `/api/generated-ads/${id}/accept-edit`, { imageUrl: newUrl }).catch((e: any) => {
                toast({
                  title: "Couldn't persist edit",
                  description: e?.message ? String(e.message) : "The edit may not survive leaving this page.",
                  variant: "destructive",
                });
                throw e;
              });
            }
            queryClient.invalidateQueries({ queryKey: ["/api/ad-generations", sessionId] });
            setEditedUrls((prev) => ({ ...prev, [id]: newUrl }));
            setLightbox((prev) => (prev && prev.adId === editingAd.id ? { ...prev, url: newUrl } : prev));
          }}
        />
      )}
    </>
  );
}

// ── Section ──────────────────────────────────────────────────────────────────

export function AdsExpressPreviousSessions({
  brandId,
  variant,
  excludeGenerationId,
  adUnits,
}: {
  brandId: string;
  variant: "standard" | "kc";
  excludeGenerationId: string | null;
  adUnits: Array<{ id: string; name: string }>;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [sectionOpen, setSectionOpen] = useState(false);

  const { data: sessions = [], isLoading } = useQuery<SessionSummary[]>({
    queryKey: [`/api/brands/${brandId}/ad-generations/recent`, variant, excludeGenerationId ?? "none"],
    queryFn: async () => {
      const params = new URLSearchParams({ variant, limit: "5" });
      if (excludeGenerationId) params.set("excludeId", excludeGenerationId);
      const res = await fetch(
        `/api/brands/${brandId}/ad-generations/recent?${params.toString()}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Failed to load previous sessions (${res.status})`);
      return res.json();
    },
    enabled: !!brandId,
    staleTime: 30_000,
  });

  const adUnitNameById = new Map(adUnits.map((u) => [u.id, u.name]));

  if (!isLoading && sessions.length === 0) return null;

  return (
    <Card className="p-5 space-y-3" data-testid="panel-previous-sessions">
      <button
        type="button"
        onClick={() => setSectionOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 text-left"
        data-testid="button-toggle-previous-sessions"
      >
        <h2 className="font-semibold flex items-center gap-2 text-sm">
          <History className="h-4 w-4 text-primary" /> Recent generations
          {!isLoading && (
            <Badge variant="secondary" className="text-[10px]" data-testid="badge-previous-sessions-count">
              {sessions.length}
            </Badge>
          )}
        </h2>
        {sectionOpen ? (
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
        )}
      </button>
      {!sectionOpen ? null : isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 rounded-lg" />
          <Skeleton className="h-14 rounded-lg" />
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((s) => {
            const meta = STATUS_META[s.status] ?? STATUS_META.completed;
            const isOpen = expandedId === s.id;
            return (
              <div
                key={s.id}
                className="rounded-lg border border-border/50 bg-card"
                data-testid={`card-previous-session-${s.id}`}
              >
                <button
                  type="button"
                  onClick={() => setExpandedId(isOpen ? null : s.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors rounded-lg"
                  data-testid={`button-toggle-session-${s.id}`}
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-semibold inline-flex items-center gap-1.5" data-testid={`text-session-date-${s.id}`}>
                        <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                        {formatWhen(s.createdAt)}
                      </span>
                      <Badge variant={meta.variant} className="text-[10px]" data-testid={`status-session-${s.id}`}>
                        {meta.label}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">
                        {s.templateCount} template{s.templateCount === 1 ? "" : "s"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2.5 flex-wrap text-[11px] text-muted-foreground min-w-0">
                      {variant === "kc" ? (
                        s.creativeDirection?.trim() ? (
                          <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
                            <Wand2 className="h-3 w-3 shrink-0" />
                            <span className="truncate" data-testid={`text-session-direction-${s.id}`}>
                              {s.creativeDirection}
                            </span>
                          </span>
                        ) : null
                      ) : (
                        <>
                          {s.personaNameSnapshot && (
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <Users className="h-3 w-3 shrink-0" />
                              <span className="truncate" data-testid={`text-session-persona-${s.id}`}>{s.personaNameSnapshot}</span>
                            </span>
                          )}
                          {s.personaCharacterSelectorEnabled === true && (
                            <Badge
                              variant="outline"
                              className="text-[10px]"
                              data-testid={`badge-session-character-${s.id}`}
                            >
                              {s.selectedPersonaCharacterNameSnapshot
                                ? `Character: ${s.selectedPersonaCharacterNameSnapshot}`
                                : "Character unavailable"}
                            </Badge>
                          )}
                          {s.angleNameSnapshot && (
                            <span className="inline-flex items-center gap-1 min-w-0">
                              <Sparkles className="h-3 w-3 shrink-0" />
                              <span className="truncate" data-testid={`text-session-angle-${s.id}`}>{s.angleNameSnapshot}</span>
                            </span>
                          )}
                        </>
                      )}
                      {s.productNameSnapshot && (
                        <span className="inline-flex items-center gap-1 min-w-0">
                          <Package className="h-3 w-3 shrink-0" />
                          <span className="truncate" data-testid={`text-session-product-${s.id}`}>{s.productNameSnapshot}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  {isOpen ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3">
                    <SessionAdsGrid sessionId={s.id} adUnitNameById={adUnitNameById} brandId={brandId} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
